import React from 'react';
import PropTypes from 'prop-types';
import RcSwitch from 'rc-switch';
import Select from 'react-select';
import styles from './styles.less';
import classNames from 'classnames';
import { Modal, Button, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';

const propTypes = {
    id: PropTypes.string,
    attribute: PropTypes.string,
    componentType: PropTypes.string,
    name: PropTypes.string,
    sizeValue: PropTypes.array,
    yAxisValue: PropTypes.string,
    showModal: PropTypes.bool,
    encodings: PropTypes.object,
    onYAxisChange: PropTypes.func,
    setEncoding: PropTypes.func,
    options: PropTypes.object
};

function filterOptions(componentType, options, blindness) {
    const out = options[componentType].color.filter(({name, blind}) => {
        if (blindness === 0) {
            return true;
        }
        if (blindness == 1) {
            return blind == 1;
        }
        if (blindness == 2) {
            return blind > 0;
        }
        //console.error(`Unexpected blindness/blind pair on ${name}: ${blind}/${blindness}`);
        if (blind === undefined) {
            return true;
        } else {
            return false;
        }
    })
    //console.log('filterOptions', {options, blindness, num: out.length});
    return out;
}

function ColorsList({ barWidth = 50, colors = [] }) {
    return (
        <span className={styles['encoding-icon-container']}>
            {(colors || []).map((color, idx, all) => (
                <span
                    key={`${idx}-${color}`}
                    style={{
                        backgroundColor: color,
                        width: `${barWidth / all.length}px`
                    }}
                />
            ))}
        </span>
    );
}

function makeOptionLists(options) {
    return {
        point: {
            color: options.point.color.map(({ name, variant, colors, label, blind }) => ({
                name,
                value: name,
                blind,
                label: (
                    <div style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
                        <ColorsList colors={colors} />
                        <label>{label}</label>
                    </div>
                )
            }))
        },
        edge: {
            color: options.edge.color.map(({ name, variant, colors, label, blind }) => ({
                name,
                value: name,
                blind,
                label: (
                    <div style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
                        <ColorsList colors={colors} />
                        <label>{label}</label>
                    </div>
                )
            }))
        },
        sizeValue: [],
        yAxisValue: 'none'
    };
}

export function isEncoded(encodings, column, encodingType) {
    return (
        encodings &&
        encodings[column.componentType] &&
        encodings[column.componentType][encodingType] &&
        encodings[column.componentType][encodingType].attribute === column.attribute
    );
}

export function isEncodedAndNotDefault(encodings, column, encodingType) {
    if (isEncoded(encodings, column, encodingType)) {
        return !encodings[column.componentType][encodingType].isDefault;
    }
    return false;
}

export class EncodingPicker extends React.Component {
    constructor(props) {
        super(props);

        this.handleSelectSizeChange = this.handleSelectSizeChange.bind(this);
        this.handleSelectColorChange = this.handleSelectColorChange.bind(this);
        this.handleSelectIconChange = this.handleSelectIconChange.bind(this);
        this.handleSelectYAxisChange = this.handleSelectYAxisChange.bind(this);
        this.handleReverseColorChange = this.handleReverseColorChange.bind(this);

        this.dispatchColorEncodingChange = this.dispatchColorEncodingChange.bind(this);
        this.dispatchIconEncodingChange = this.dispatchIconEncodingChange.bind(this);

        this.setBlindness = this.setBlindness.bind(this);
        this.close = this.close.bind(this);
        this.open = this.open.bind(this);

        this.state = {
            showModal: false,
            blindness: 0
        };

        if (props.options) {
            this.options = makeOptionLists(props.options);
        }
    }

    dispatchIconEncodingChange({ prop, val }) {
        if (!this.props.setEncoding) return;

        const reset = prop === 'icon' ? !val : !isEncoded(this.props.encodings, this.props, 'icon');

        return this.props.setEncoding({
            encodingType: 'icon',
            reset,
            graphType: this.props.componentType,
            attribute: this.props.attribute
        });
    }

    dispatchColorEncodingChange({ prop, val }) {
        if (!this.props.setEncoding) return;

        const graphType = this.props.componentType;

        const name = //palette; reuse if prop != color
            prop === 'color'
                ? val
                : isEncoded(this.props.encodings, this.props, 'color')
                  ? this.props.encodings[graphType].color.name
                  : undefined;
        const { variant: variation, colors, label } = name
            ? this.props.options[this.props.componentType].color
                  .filter(({ name: optName }) => name === optName)
                  .concat([{ variant: 'categorical' }])[0]
            : {};
        const nameOpts = { reset: !name, name, variation, colors };

        const reverseColorOpts = {
            reverse:
                prop === 'reverseColor'
                    ? val
                    : isEncoded(this.props.encodings, this.props, 'color')
                      ? this.props.encodings[graphType].color.reverse === true
                      : false
        };

        return this.props.setEncoding({
            encodingType: 'color',
            graphType,
            attribute: this.props.attribute,
            ...reverseColorOpts,
            ...nameOpts
        });
    }

    handleSelectColorChange(val) {
        console.debug('handleSelectColorChange', {val});
        if (!val) {
            return this.props.setEncoding({
                encodingType: 'color',
                graphType: this.props.componentType,
                attribute: this.props.attribute,
                reset: true
            })
        }
        const {name} = val;
        return this.dispatchColorEncodingChange({ prop: 'color', val: name });
    }

    handleReverseColorChange(reverseColor) {
        return this.dispatchColorEncodingChange({ prop: 'reverseColor', val: reverseColor });
    }

    handleSelectIconChange(newEnabled) {
        return this.dispatchIconEncodingChange({ prop: 'icon', val: newEnabled });
    }

    handleSelectYAxisChange(yAxisValue) {
        if (this.props.onYAxisChange) {
            this.props.onYAxisChange(yAxisValue || 'none');
        }
    }

    handleSelectSizeChange(newEnabled) {
        if (!this.props.setEncoding) return;

        // No variation for sizes :/
        const reset = !newEnabled;
        const name = this.props.componentType + 'Size';
        const encodingType = 'size';
        const graphType = this.props.componentType;
        const attribute = this.props.attribute;

        this.props.setEncoding({
            name,
            encodingType,
            graphType,
            attribute,
            reset
        });
    }

    close() {
        this.setState({ ...this.state, showModal: false });
    }

    open() {
        this.setState({ ...this.state, showModal: true });
    }

    setBlindness(v) {
        this.setState({ ...this.state, blindness: v});
    }

    render() {
        const { props, options } = this;
        //console.info('options', {options, propsOptions: props.options});
        const { encodings, componentType } = props;

        if (!options || !encodings || !componentType) {
            return null;
        }

        let colorEncodingValue = {};
        let showReverseColorPaletteSwitch = false;
        let showEncodeSizesSwitch =
            !isEncoded(encodings, props, 'size') ||
            isEncodedAndNotDefault(encodings, props, 'size');
        let showEncodeIconsSwitch =
            !isEncoded(encodings, props, 'icon') ||
            isEncodedAndNotDefault(encodings, props, 'icon');

        if (isEncoded(encodings, props, 'color')) {
            showReverseColorPaletteSwitch = true;
            const colorEncoding = encodings[componentType].color;
            colorEncodingValue = options[componentType].color.find(
                ({ name }) => name === colorEncoding.name
            );
            if (!colorEncodingValue && colorEncoding.legend && colorEncoding.legend.length) {
                showReverseColorPaletteSwitch = false;
                colorEncodingValue = {
                    value: 'Default',
                    label: (
                        <div style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
                            <ColorsList colors={colorEncoding.legend} />
                            <label>Default</label>
                        </div>
                    )
                };
            }
        }

        const blindessOptions = [
            {value: 0, name: 'blind0name', label: 'Show all colors: no color blind safety'},
            {value: 1, name: 'blind1name', label: 'Show color blind safe colors'},
            {value: 2, name: 'blind2name', label: 'Show color blind + partial color blind safe colors'}
        ];

        return (
            <span id={props.id} name={props.name || props.id}>
                <OverlayTrigger
                    placement="top"
                    delayShow={350}
                    trigger={['hover', 'focus']} // <-- do this so react bootstrap doesn't complain about accessibility
                    style={{ zIndex: 999999999 }}
                    overlay={<Tooltip id={`${props.id}_tooltip`}>Set coloring</Tooltip>}>


                    <Button className={`${styles['histogram-legend-pill']} ${styles[isEncodedAndNotDefault(
                                encodings,
                                { componentType, attribute: props.attribute },
                                'color'
                            ) ? 'on' : 'off']}`} size="xsmall"
                        onClick={this.open}
                    >
                        <i
                            className={classNames({
                                fa: true,
                                'fa-paint-brush': true,
                                [styles['encoding-picker-button']]: true
                            })}
                        />
                    </Button>
                </OverlayTrigger>

                <Modal
                    show={this.state.showModal}
                    onHide={this.close}
                    style={{ zIndex: 999999999 }}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Visualize{' '}
                            <b>
                                {componentType}:{props.attribute}
                            </b>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5>Show using color</h5>
                        <Select
                            simpleValue
                            isDisabled={false}
                            value={colorEncodingValue}
                            placeholder={"Pick how to visualize"}
                            options={filterOptions(componentType, options, this.state.blindness)}
                            id={`${props.id}_select`}
                            name={`${props.name || props.id}_select`}
                            optionRenderer={({ value, label }) => label}
                            onChange={this.handleSelectColorChange}
                            isClearable={showReverseColorPaletteSwitch}
                        />
                        <Select
                            simpleValue
                            isDisabled={false}
                            value={blindessOptions[this.state.blindness]}
                            options={blindessOptions}
                            id={`${props.id}_blindness`}
                            placeholder={"Configure color blindness"}
                            onChange={(v) => {
                                const { label, name, value } = v;
                                this.setBlindness(value);
                            }}
                            isClearable={false}
                        />
                        {(showReverseColorPaletteSwitch && (
                            <div>
                                <h5>Reverse color palette order</h5>
                                <RcSwitch
                                    checked={
                                        isEncoded(encodings, props, 'color')
                                            ? encodings[componentType].color.reverse
                                            : false
                                    }
                                    checkedChildren={'On'}
                                    unCheckedChildren={'Off'}
                                    onChange={this.handleReverseColorChange}
                                />
                            </div>
                        )) ||
                            undefined}
                        {(showEncodeIconsSwitch && (
                            <div>
                                <h5>Show using icon</h5>
                                <RcSwitch
                                    checked={isEncoded(encodings, props, 'icon')}
                                    checkedChildren={'On'}
                                    unCheckedChildren={'Off'}
                                    onChange={this.handleSelectIconChange}
                                />
                                <p>
                                    Pick icons by setting field values that are{' '}
                                    <a href="http://fontawesome.io/icons/">Font Awesome</a> icon
                                    names. Example: <q>laptop</q>
                                </p>
                            </div>
                        )) ||
                            undefined}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.close}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </span>
        );
    }
}

EncodingPicker.propTypes = propTypes;

export default EncodingPicker;
